/**
 * Severity Distribution Widget
 * Donut chart matching the screenshot
 */

interface SeverityData {
  high: { count: number; percentage: string }
  medium: { count: number; percentage: string }
  low: { count: number; percentage: string }
  total: number
}

interface SeverityDistributionWidgetProps {
  data: SeverityData
  usesCustomScope?: boolean
  isEditMode?: boolean
}

export function SeverityDistributionWidget({ data, usesCustomScope, isEditMode }: SeverityDistributionWidgetProps) {
  const highPct = parseFloat(data.high.percentage)
  const mediumPct = parseFloat(data.medium.percentage)
  const lowPct = parseFloat(data.low.percentage)

  // SVG donut chart angles
  const highAngle = (highPct / 100) * 360
  const mediumAngle = (mediumPct / 100) * 360
  const lowAngle = (lowPct / 100) * 360

  const describeArc = (startAngle: number, endAngle: number, radius: number = 80, innerRadius: number = 50) => {
    const start = polarToCartesian(100, 100, radius, endAngle)
    const end = polarToCartesian(100, 100, radius, startAngle)
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1'

    const innerStart = polarToCartesian(100, 100, innerRadius, endAngle)
    const innerEnd = polarToCartesian(100, 100, innerRadius, startAngle)

    return [
      'M',
      start.x,
      start.y,
      'A',
      radius,
      radius,
      0,
      largeArcFlag,
      0,
      end.x,
      end.y,
      'L',
      innerEnd.x,
      innerEnd.y,
      'A',
      innerRadius,
      innerRadius,
      0,
      largeArcFlag,
      1,
      innerStart.x,
      innerStart.y,
      'Z',
    ].join(' ')
  }

  const polarToCartesian = (cx: number, cy: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0
    return {
      x: cx + radius * Math.cos(angleInRadians),
      y: cy + radius * Math.sin(angleInRadians),
    }
  }

  let currentAngle = 0
  const highPath = describeArc(currentAngle, currentAngle + highAngle)
  currentAngle += highAngle
  const mediumPath = describeArc(currentAngle, currentAngle + mediumAngle)
  currentAngle += mediumAngle
  const lowPath = describeArc(currentAngle, currentAngle + lowAngle)

  return (
    <div className="relative flex h-full flex-col overflow-hidden rounded-lg border border-[#e6e6e6] bg-white">
      {usesCustomScope && !isEditMode && (
        <div className="absolute right-5 top-5 z-10">
          <div className="flex items-center rounded-3xl border border-[#ff8637] bg-[#fff7ed] px-2 py-1">
            <span className="text-[10px] font-bold leading-[15px] tracking-[0.1px] text-[#c83408]">
              Custom Scope
            </span>
          </div>
        </div>
      )}

      <div className="flex flex-1 flex-col gap-4 p-5">
        <h3 className="text-xl font-medium leading-[30px] tracking-[0.2px] text-[#1d1d1d]">
          Severity Distribution
        </h3>

        {/* Donut Chart - Centered */}
        <div className="flex flex-1 items-center justify-center">
          <svg
            viewBox="0 0 200 200"
            className="h-56 w-56"
          >
            {/* High severity */}
            {highPct > 0 && (
              <path
                d={highPath}
                fill="#dc2626"
                className="transition-opacity hover:opacity-80"
              />
            )}
            {/* Medium severity */}
            {mediumPct > 0 && (
              <path
                d={mediumPath}
                fill="#f97316"
                className="transition-opacity hover:opacity-80"
              />
            )}
            {/* Low severity */}
            {lowPct > 0 && (
              <path
                d={lowPath}
                fill="#eab308"
                className="transition-opacity hover:opacity-80"
              />
            )}
          </svg>
        </div>

        {/* Legend - Horizontal Row at Bottom */}
        <div className="flex items-center justify-around border-t border-[#e6e6e6] pt-4">
          {highPct > 0 && (
            <div className="flex items-center gap-2">
              <div className="h-[10px] w-[10px] rounded-full bg-[#dc2626]" />
              <span className="text-sm font-medium leading-[21px] tracking-[0.14px] text-[#535353]">High</span>
              <span className="text-xl font-semibold leading-none text-[#1d1d1d]">{data.high.count}</span>
            </div>
          )}
          {mediumPct > 0 && (
            <div className="flex items-center gap-2">
              <div className="h-[10px] w-[10px] rounded-full bg-[#f97316]" />
              <span className="text-sm font-medium leading-[21px] tracking-[0.14px] text-[#535353]">Medium</span>
              <span className="text-xl font-semibold leading-none text-[#1d1d1d]">{data.medium.count}</span>
            </div>
          )}
          {lowPct > 0 && (
            <div className="flex items-center gap-2">
              <div className="h-[10px] w-[10px] rounded-full bg-[#eab308]" />
              <span className="text-sm font-medium leading-[21px] tracking-[0.14px] text-[#535353]">Low</span>
              <span className="text-xl font-semibold leading-none text-[#1d1d1d]">{data.low.count}</span>
            </div>
          )}
          <div className="flex items-center gap-2 border-l border-[#e6e6e6] pl-4">
            <span className="text-sm font-medium leading-[21px] tracking-[0.14px] text-[#535353]">Total</span>
            <span className="text-xl font-semibold leading-none text-[#1d1d1d]">{data.total}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
